import React, { useState, useEffect } from 'react';
import { 
  RotateCcw, 
  Sparkles, 
  BrainCircuit, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Play, 
  Flame, 
  Target,
  AlertTriangle,
  BookOpen,
  HelpCircle,
  Layers
} from 'lucide-react';
import { UserProfile, Question, QuestionMastery, PraxisPaper } from '../types';
import { PRAXIS_PAPERS } from '../data/praxisPapers';
import { getPaperCatalogSummaries, getQuestionsForStudiedPapers, getUserQuestionMastery, isPaperUnlockedForUser } from '../lib/firebase';
import { getDueQuestions, generateSmartPracticeQuestions } from '../lib/adaptiveSequencing';

interface ReviewQueueProps {
  userProfile: UserProfile;
  onStartCustomPractice: (questions: Question[], title: string, mode?: 'exam' | 'practice') => void;
  onOpenPricingModal: () => void;
}

export const ReviewQueue: React.FC<ReviewQueueProps> = ({
  userProfile,
  onStartCustomPractice,
  onOpenPricingModal
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [masteryList, setMasteryList] = useState<QuestionMastery[]>([]);
  const [allQuestions, setAllQuestions] = useState<Question[]>([]);
  const [papers, setPapers] = useState<PraxisPaper[]>(PRAXIS_PAPERS);
  const [selectedFilter, setSelectedFilter] = useState<'due' | 'upcoming' | 'mastered'>('due');

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        // 2026-10-08 speed round: this used to download every question of
        // every paper (getPraxisPapersFromFirestore) to find the few hundred
        // this student has answered. Now: paper details from the light
        // catalog, the student's review records, and then ONLY the question
        // pools of papers they have actually practised, all fetched in
        // parallel where possible.
        const [catalog, mastery] = await Promise.all([
          getPaperCatalogSummaries(),
          userProfile.uid ? getUserQuestionMastery(userProfile.uid) : Promise.resolve([] as QuestionMastery[])
        ]);
        if (catalog && catalog.length > 0) {
          setPapers(catalog);
        }
        setMasteryList(mastery);

        const isUnlocked = isPaperUnlockedForUser(userProfile);
        if (isUnlocked && userProfile.uid) {
          const studied = await getQuestionsForStudiedPapers(userProfile.uid, mastery);
          setAllQuestions(studied);
        } else {
          setAllQuestions([]);
        }
      } catch (err) {
        console.warn('Error loading review queue data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [userProfile.uid, userProfile.tier]);

  const { dueQuestions, dueMasteryMap } = getDueQuestions(masteryList, allQuestions);

  // Stats calculation
  const totalMasteryCount = masteryList.length;
  const masteredItems = masteryList.filter(m => (m.consecutiveCorrect || 0) >= 2 || (m.timesCorrect > 2 && m.lastResult === 'correct'));
  
  // Calculate domain accuracy
  const domainBreakdown: Record<string, { correct: number; total: number }> = {};
  const questionMap = new Map<string, Question>(allQuestions.map(q => [q.id, q]));

  for (const m of masteryList) {
    const q = questionMap.get(m.questionId);
    const domain = q?.domain || 'General';
    if (!domainBreakdown[domain]) {
      domainBreakdown[domain] = { correct: 0, total: 0 };
    }
    domainBreakdown[domain].correct += m.timesCorrect || 0;
    domainBreakdown[domain].total += (m.timesCorrect || 0) + (m.timesIncorrect || 0);
  }

  let weakestDomainName = 'None Yet';
  let lowestAccuracy = 100;
  for (const [dom, stats] of Object.entries(domainBreakdown)) {
    if (stats.total > 0) {
      const acc = Math.round((stats.correct / stats.total) * 100);
      if (acc < lowestAccuracy) {
        lowestAccuracy = acc;
        weakestDomainName = dom;
      }
    }
  }

  const handleStartDueReview = () => {
    if (dueQuestions.length === 0) return;
    onStartCustomPractice(
      dueQuestions,
      `Spaced Repetition Review (${dueQuestions.length} Items Due)`,
      'practice'
    );
  };

  const handleStartSmartPractice = () => {
    const smartQuestions = generateSmartPracticeQuestions(masteryList, allQuestions, 10);
    if (smartQuestions.length === 0) return;
    onStartCustomPractice(
      smartQuestions,
      'Smart Practice: Weakness-Targeted Drill (10 Questions)',
      'practice'
    );
  };

  const formatDueTime = (isoString: string | null) => {
    if (!isoString) return 'Not scheduled';
    const dueTime = new Date(isoString).getTime();
    const diffHours = Math.round((dueTime - Date.now()) / (1000 * 60 * 60));
    if (diffHours < 0) {
      const pastHours = Math.abs(diffHours);
      if (pastHours < 24) return `${pastHours} hr${pastHours === 1 ? '' : 's'} ago`;
      const pastDays = Math.floor(pastHours / 24);
      return `${pastDays} day${pastDays === 1 ? '' : 's'} ago`;
    } else {
      if (diffHours < 24) return `In ${diffHours} hr${diffHours === 1 ? '' : 's'}`;
      const futureDays = Math.floor(diffHours / 24);
      return `In ${futureDays} day${futureDays === 1 ? '' : 's'}`;
    }
  };

  // Filter queue items
  const nowMs = Date.now();
  const queueItems = masteryList.filter(m => {
    const isDue = m.nextReviewDue && new Date(m.nextReviewDue).getTime() <= nowMs;
    if (selectedFilter === 'due') return isDue;
    if (selectedFilter === 'upcoming') return m.nextReviewDue && new Date(m.nextReviewDue).getTime() > nowMs;
    if (selectedFilter === 'mastered') return (m.consecutiveCorrect || 0) >= 2 || (m.timesCorrect > 2 && m.lastResult === 'correct');
    return true;
  });

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-8">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white rounded-2xl p-6 sm:p-8 shadow-xl border border-indigo-900/40 relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-8 -mr-8 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="flex items-center gap-2 text-xs font-bold text-indigo-300 uppercase tracking-wider">
              <RotateCcw className="w-4 h-4 text-indigo-400" /> SM-2 Spaced Repetition Engine
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              Adaptive Review Queue & Weakness Drills
            </h1>
            <p className="text-slate-300 text-sm leading-relaxed">
              Questions are scheduled for review at optimal memory retention intervals (1 → 3 → 7 → 14 → 30 days). Correct reviews push items out; incorrect reviews reset them immediately for mastery reinforcement.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <button
              onClick={handleStartSmartPractice}
              disabled={allQuestions.length === 0}
              className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs sm:text-sm px-4 py-2.5 rounded-xl transition shadow-md flex items-center gap-2 border border-indigo-400/30"
            >
              <Sparkles className="w-4 h-4 text-amber-300" />
              <span>Smart Practice (10-Q Drill)</span>
            </button>

            {dueQuestions.length > 0 && (
              <button
                onClick={handleStartDueReview}
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs sm:text-sm px-4 py-2.5 rounded-xl transition shadow-md flex items-center gap-2"
              >
                <Play className="w-4 h-4 text-slate-950 fill-slate-950" />
                <span>Review {dueQuestions.length} Due Questions</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Metric Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Due Now */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
          <div className="space-y-1">
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Due For Review
            </div>
            <div className="text-2xl font-black text-slate-800 flex items-center gap-2">
              {dueQuestions.length}
              {dueQuestions.length > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold animate-pulse">
                  Action Due
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 font-medium">
              Scheduled for immediate recall
            </div>
          </div>
          <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
            <RotateCcw className="w-6 h-6 text-amber-500" />
          </div>
        </div>

        {/* Card 2: Total Attempted */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
          <div className="space-y-1">
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Questions Tracked
            </div>
            <div className="text-2xl font-black text-slate-800">
              {totalMasteryCount}
            </div>
            <div className="text-[11px] text-slate-500 font-medium">
              In personalized memory store
            </div>
          </div>
          <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
            <Layers className="w-6 h-6 text-indigo-600" />
          </div>
        </div>

        {/* Card 3: Mastered Items */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
          <div className="space-y-1">
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Mastered Items
            </div>
            <div className="text-2xl font-black text-emerald-600">
              {masteredItems.length}
            </div>
            <div className="text-[11px] text-slate-500 font-medium">
              Extended memory interval (7d+)
            </div>
          </div>
          <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
          </div>
        </div>

        {/* Card 4: Weakest Domain */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
          <div className="space-y-1 min-w-0">
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Focus Weakness
            </div>
            <div className="text-sm font-bold text-slate-800 truncate" title={weakestDomainName}>
              {weakestDomainName}
            </div>
            <div className="text-[11px] text-slate-500 font-medium">
              Accuracy: <span className="font-bold text-rose-600">{lowestAccuracy < 100 ? `${lowestAccuracy}%` : 'N/A'}</span>
            </div>
          </div>
          <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center font-bold shrink-0">
            <Target className="w-6 h-6 text-rose-500" />
          </div>
        </div>
      </div>

      {/* Tab Filter Controls */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-none">
          {[
            { id: 'due', label: `Due For Review (${dueQuestions.length})` },
            { id: 'upcoming', label: `Scheduled Upcoming (${masteryList.length - dueQuestions.length})` },
            { id: 'mastered', label: `Mastered Items (${masteredItems.length})` }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setSelectedFilter(tab.id as any)}
              className={`px-4 py-2 rounded-full text-xs font-bold transition-all shrink-0 ${
                selectedFilter === tab.id
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200/70'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="text-xs text-slate-500 font-medium">
          Interval schedule: <span className="font-bold text-indigo-600">1d → 3d → 7d → 14d → 30d</span>
        </div>
      </div>

      {/* Queue Items Table / Empty State */}
      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500 space-y-3">
          <div className="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="font-semibold text-slate-700 text-sm">Loading spaced repetition data...</p>
        </div>
      ) : queueItems.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
          </div>
          <div className="space-y-1">
            <h3 className="text-lg font-bold text-slate-800">
              {selectedFilter === 'due'
                ? '🎉 Spaced Repetition Queue Clear!'
                : 'No items in this category yet'}
            </h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
              {selectedFilter === 'due'
                ? 'You have answered all due questions for today. Keep taking practice tests or launch a Smart Practice drill to continue building memory retention.'
                : 'Complete more questions in timed exam mode or practice mode to build your memory schedule.'}
            </p>
          </div>
          <div className="pt-2 flex justify-center gap-3">
            <button
              onClick={handleStartSmartPractice}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition shadow-xs flex items-center gap-1.5"
            >
              <Sparkles className="w-4 h-4 text-amber-300" /> Start Smart Practice Drill
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  <th className="py-3 px-4">Question & Domain</th>
                  <th className="py-3 px-4">Last Result</th>
                  <th className="py-3 px-4">Accuracy Record</th>
                  <th className="py-3 px-4">Current Interval</th>
                  <th className="py-3 px-4">Next Review Due</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs text-slate-700 font-medium">
                {queueItems.map((item, idx) => {
                  const q = questionMap.get(item.questionId);
                  const isDueNow = item.nextReviewDue && new Date(item.nextReviewDue).getTime() <= nowMs;

                  return (
                    <tr key={item.questionId || idx} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3.5 px-4 space-y-1 max-w-md">
                        <div className="font-bold text-slate-800 line-clamp-2">
                          {q ? q.questionText : `Question #${item.questionId}`}
                        </div>
                        <div className="text-[11px] text-indigo-600 font-semibold flex items-center gap-1">
                          <BookOpen className="w-3 h-3" />
                          <span>{q?.domain || 'General Domain'}</span>
                        </div>
                      </td>

                      <td className="py-3.5 px-4 whitespace-nowrap">
                        {item.lastResult === 'correct' ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Correct
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                            <XCircle className="w-3 h-3 text-rose-600" /> Missed
                          </span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <div className="font-bold text-slate-800">
                          {item.timesCorrect} correct / {item.timesIncorrect} missed
                        </div>
                        <div className="text-[10px] text-slate-400">
                          Streak: {item.consecutiveCorrect || 0} in a row
                        </div>
                      </td>

                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <span className="px-2 py-1 bg-slate-100 text-slate-700 font-bold text-[11px] rounded">
                          {item.intervalDays || 1} day{(item.intervalDays || 1) === 1 ? '' : 's'}
                        </span>
                      </td>

                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <div className={`font-bold flex items-center gap-1 ${isDueNow ? 'text-amber-600' : 'text-slate-600'}`}>
                          <Clock className="w-3.5 h-3.5" />
                          <span>{formatDueTime(item.nextReviewDue)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
